import React, { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle } from 'lucide-react';
import { API_URL } from '../config/env';
import { ministriesAPI, officesAPI } from '../services/api';

interface Ministry { id: number; name: string; isActive: boolean; }
interface Office   { id: number; name: string; isActive?: boolean; }

interface Props {
  onClose: () => void;
  onSuccess?: (grievanceId: string) => void;
}

const categories = [
  { value: 'ADMINISTRATIVE', label: 'Administrative Issues' },
  { value: 'INFRASTRUCTURE', label: 'Infrastructure Problems' },
  { value: 'HEALTH',         label: 'Health Services' },
  { value: 'EDUCATION',      label: 'Education' },
  { value: 'SECURITY',       label: 'Security & Safety' },
  { value: 'ENVIRONMENT',    label: 'Environment' },
  { value: 'OTHER',          label: 'Other' },
];

const AdminAddGrievanceModal: React.FC<Props> = ({ onClose, onSuccess }) => {
  const [ministries, setMinistries]     = useState<Ministry[]>([]);
  const [offices,    setOffices]        = useState<Office[]>([]);
  const [loadingOffices, setLoadingOffices] = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [successId,  setSuccessId]      = useState<string | null>(null);
  const [errors,     setErrors]         = useState<Record<string, string>>({});

  const [form, setForm] = useState({
    isAnonymous:       false,
    fullName:          '',
    email:             '',
    phone:             '',
    address:           '',
    district:          '',
    ministryId:        '' as number | '',
    officeId:          '' as number | '',
    subject:           '',
    description:       '',
    category:          '',
    priority:          'MEDIUM' as 'LOW' | 'MEDIUM' | 'HIGH',
    preferredLanguage: 'en' as 'en' | 'ne',
  });

  useEffect(() => {
    ministriesAPI.getAll()
      .then(r => setMinistries((r.data || []).filter((m: Ministry) => m.isActive !== false)))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!form.ministryId) { setOffices([]); return; }
    setLoadingOffices(true);
    officesAPI.byMinistry(form.ministryId as number)
      .then(r => setOffices((r.data || []).filter((o: Office) => o.isActive !== false)))
      .catch(() => setOffices([]))
      .finally(() => setLoadingOffices(false));
  }, [form.ministryId]);

  const set = (field: string, value: unknown) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.isAnonymous && !form.fullName.trim()) e.fullName    = 'Full name is required';
    if (!form.description.trim())                   e.description = 'Description is required';
    if (!form.category)                             e.category    = 'Category is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('isAnonymous',       form.isAnonymous.toString());
      fd.append('fullName',          form.fullName);
      fd.append('email',             form.email);
      fd.append('phone',             form.phone);
      fd.append('address',           form.address);
      fd.append('district',          form.district);
      if (form.ministryId) fd.append('ministry_id', String(form.ministryId));
      if (form.officeId)   fd.append('office_id',   String(form.officeId));
      fd.append('subject',           form.subject);
      fd.append('description',       form.description);
      fd.append('category',          form.category);
      fd.append('priority',          form.priority);
      fd.append('preferredLanguage', form.preferredLanguage);

      const res  = await fetch(`${API_URL}/grievances`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to submit');
      setSuccessId(data.grievanceId);
      onSuccess?.(data.grievanceId);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to submit grievance');
    } finally {
      setSubmitting(false);
    }
  };

  if (successId) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Grievance Submitted!</h3>
          <p className="text-gray-500 mb-4 text-sm">The grievance has been registered successfully.</p>
          <div className="bg-gray-50 rounded-lg p-3 mb-6">
            <p className="text-xs text-gray-500 mb-1">Grievance ID</p>
            <p className="text-lg font-mono font-bold text-primary-600">{successId}</p>
          </div>
          <button onClick={onClose} className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">Add Grievance Manually</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Anonymous toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={form.isAnonymous} onChange={e => set('isAnonymous', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600" />
            <span className="text-sm font-medium text-gray-700">Submit Anonymously</span>
          </label>

          {/* Personal info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name {!form.isAnonymous && <span className="text-red-500">*</span>}
              </label>
              <input type="text" disabled={form.isAnonymous} value={form.fullName}
                onChange={e => set('fullName', e.target.value)}
                placeholder={form.isAnonymous ? 'Disabled (Anonymous)' : 'Enter full name'}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500
                  ${form.isAnonymous ? 'bg-gray-100 cursor-not-allowed' : ''}
                  ${errors.fullName  ? 'border-red-400' : 'border-gray-300'}`} />
              {errors.fullName && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.fullName}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" disabled={form.isAnonymous} value={form.email}
                onChange={e => set('email', e.target.value)} placeholder="email@example.com"
                className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500
                  ${form.isAnonymous ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input type="tel" disabled={form.isAnonymous} value={form.phone}
                onChange={e => set('phone', e.target.value)} placeholder="98XXXXXXXX"
                className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500
                  ${form.isAnonymous ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
              <input type="text" disabled={form.isAnonymous} value={form.district}
                onChange={e => set('district', e.target.value)} placeholder="e.g. Kathmandu"
                className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500
                  ${form.isAnonymous ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
              <input type="text" disabled={form.isAnonymous} value={form.address}
                onChange={e => set('address', e.target.value)} placeholder="Full address"
                className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500
                  ${form.isAnonymous ? 'bg-gray-100 cursor-not-allowed' : ''}`} />
            </div>
          </div>

          {/* Ministry / Office */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ministry</label>
              <select value={form.ministryId}
                onChange={e => { set('ministryId', e.target.value ? Number(e.target.value) : ''); set('officeId', ''); }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">Select Ministry</option>
                {ministries.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Office</label>
              <select value={form.officeId}
                onChange={e => set('officeId', e.target.value ? Number(e.target.value) : '')}
                disabled={!form.ministryId || loadingOffices}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100">
                <option value="">{loadingOffices ? 'Loading…' : 'Select Office'}</option>
                {offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          </div>

          {/* Grievance details */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input type="text" value={form.subject} onChange={e => set('subject', e.target.value)}
                placeholder="Brief description of the grievance"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                <select value={form.category} onChange={e => set('category', e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500
                    ${errors.category ? 'border-red-400' : 'border-gray-300'}`}>
                  <option value="">Select Category</option>
                  {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                {errors.category && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.category}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select value={form.priority} onChange={e => set('priority', e.target.value as 'LOW' | 'MEDIUM' | 'HIGH')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
              <textarea rows={4} value={form.description} onChange={e => set('description', e.target.value)}
                placeholder="Describe the grievance in detail…"
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500
                  ${errors.description ? 'border-red-400' : 'border-gray-300'}`} />
              {errors.description && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.description}</p>}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2 border-t">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition font-medium">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="px-5 py-2 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition font-medium disabled:opacity-60">
              {submitting ? 'Submitting…' : 'Submit Grievance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAddGrievanceModal;
